/**
 * Logo Garden Carousel module
 *
 */

// Deps
import { createComponent, FelaComponent, RendererContext } from "react-fela";
import { gutters } from "~/styleguide";
import { fluid } from "~/styleguide/utils";
import { themeText, textStyles } from "~/styleguide/typography";
import { combineRules } from "fela";
import get from "lodash.get";
import moduleData from "~/data/module-data.json";

// Context
import { getThemeForModule } from "~/context/theme";
import WordpressLink from "~/components/global/links";

import { useContext, useEffect, useState } from "react";

let animationName = "";

// Components
import Visual from "~/components/shared/visual";

export default function LogoGarden({ block, isLcpCandidate }) {

    const [animationReady, setAnimationReady] = useState(false);
    const renderer = useContext(RendererContext);
    animationName = renderer.renderKeyframe(keyframes, {});

    let {
        use_global_content,
        heading,
        subheading,
        theme_color,
        logos,
        anchor_id,
        carousel_speed,
        preload
    } = block;

    let { layout } = block;

    if (use_global_content) {
        // Grab the data from the Page or the Globals

        const contentObject = moduleData;
        const keyPrefix = use_global_content ? "data." : "";

        heading = get(contentObject, `${keyPrefix}acf.carousel_logo_heading`);
        subheading = get(contentObject, `${keyPrefix}acf.carousel_logo_subheading`);
        carousel_speed = get(contentObject, `${keyPrefix}acf.carousel_logo_speed`);
        logos = get(contentObject, `${keyPrefix}acf.carousel_logos`);
    }

    // Determine color theme
    const theme = getThemeForModule(theme_color);
    const textTheme = themeText(theme);
    const shouldPreloadLogos =
        isLcpCandidate === true || (isLcpCandidate === undefined && preload);
    const logoCount = logos && logos.length ? logos.length : 0;

    useEffect(() => {
        if (typeof window === "undefined") return;

        let idleHandle = null;
        let timeoutHandle = null;

        const isMobile =
            window.matchMedia &&
            window.matchMedia("(max-width: 767px)").matches;

        if (!isMobile) {
            setAnimationReady(true);
            return;
        }

        const startAnimation = () => {
            if (window.requestIdleCallback) {
                idleHandle = window.requestIdleCallback(() => {
                    setAnimationReady(true);
                });
            } else {
                timeoutHandle = window.setTimeout(() => {
                    setAnimationReady(true);
                }, 1);
            }
        };

        if (document.readyState === "complete") {
            startAnimation();
            return;
        }

        window.addEventListener("load", startAnimation, { once: true });

        return () => {
            window.removeEventListener("load", startAnimation);

            if (idleHandle && window.cancelIdleCallback) {
                window.cancelIdleCallback(idleHandle);
            }

            if (timeoutHandle) {
                window.clearTimeout(timeoutHandle);
            }
        };
    }, []);

    const carouselSlider = () => {
        return (
            <LogoContainer
                animate={animationReady}
                className="slidetrack"
                contentAbove={heading || subheading}
                layout={layout}
                length={logoCount}
                speed={carousel_speed}
            >
                {logos &&
                    logos.length &&
                    logos.map((logo, index) => {
                        if (!logo || !logo.logo) return null;
                        if (logo.link_content && logo.link_content.length > 0){
                          return (
                              <WordpressLink
                                  url = {logo.link_content[0].link}
                              >
                                  {logo.logo.alt &&
                                    <span style={{ display: "none" }}>{logo.logo.alt}</span>
                                  }
                                  <Visual
                                      extend={logoStyle({
                                          collectionLength: (logos.length),
                                          layout: layout
                                      })}
                                      load={shouldPreloadLogos}
                                      image={logo.logo}
                                      key={index}
                                  />
                              </WordpressLink>
                          );
                        } else {
                          return (
                                  <Visual
                                      extend={logoStyle({
                                          collectionLength: (logos.length),
                                          layout: layout
                                      })}
                                      load={shouldPreloadLogos}
                                      image={logo.logo}
                                      key={index}
                                  />
                          );
                        }
                })}
            </LogoContainer>
        )
    }

    return (
        <Container id={anchor_id} className="module-logo-garden-carousel">
            {/* Subheading */}
            {heading && (
                <FelaComponent
                    as="h3"
                    style={combineRules(
                        textStyles.heading3,
                        textTheme,
                        gardenHeadingStyle
                    )}
                    dangerouslySetInnerHTML={{
                        __html: heading
                    }}
                >
                    <span
                    dangerouslySetInnerHTML={{
                        __html: heading
                    }} />
                </FelaComponent>
            )}

            {/* Subheading */}
            {subheading && (
                <FelaComponent
                    as="p"
                    style={combineRules(
                        textStyles.paragraphLarge,
                        textTheme,
                        gardenSubheadingStyle
                    )}
                >
                    <span
                    dangerouslySetInnerHTML={{
                        __html: subheading
                    }} />
                </FelaComponent>
            )}
            <SliderContainer className="slider">
                {carouselSlider()}
                {animationReady && carouselSlider()}
            </SliderContainer>
        </Container>
    );
}

const gardenSubheadingStyle = {
    width: "100%",
    marginTop: "15px"
};

const gardenHeadingStyle = () => ({
    width: "100%",
    marginBottom: 10,
    extend: [fluid("letter-spacing", { max: 2, min: 0.5 })]
});

const logoStyle = () => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 75,
    marginBottom: 20,
    width: 200,
    maxWidthTablet: {
        width: 150,
    },
    maxWidthMobile: {
        marginRight: 30,
    },
    "& img": {
        maxHeight: 67,
        maxWidth: "100%",
        padding: 1
    }
});

const Container = createComponent(
    () => ({
        textAlign: "center",
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        margin: "0 30px",
        borderRadius: "30px",
        "> *": {
            position: "relative",
            zIndex: 1
        },
        extend: [
            fluid("paddingV", gutters.vertical),
            fluid("paddingH", gutters.default)
        ]
    }),
    "div",
    ["id"]
);

const keyframes = () => ({
    "0%": { transform: "translateX(0%);" },
    "100%": { transform: "translateX(-100%);" }
});

const LogoContainer = createComponent(({ animate, length, contentAbove, speed }) => ({
    display: "flex",
    justifyContent: "center",
    flexFlow: "row nowrap",
    alignItems: "center",
    alignContent: "flex-start",
    position: "relative",
    margin: "auto",
    maxWidth: "inherit",
    left:0,
    "> *": {
        flexShrink: 0
    },
    extend: [
        {
            condition: animate && length && speed,
            style: {
                animationDuration: `${length * speed}s`,
                animationIterationCount: "infinite",
                animationTimingFunction: "linear",
                animationName: `${animationName}`
            }
        },
        {
            condition: contentAbove,
            style: {
                extend: [fluid("marginTop", { min: 30, max: 50 })]
            }
        }
    ]
}));

const SliderContainer = createComponent(
    () => ({
        display: "flex",
        flexFlow: "row nowrap",
        overflow: "hidden",
        position: "relative",
        "::before": {
            background: "linear-gradient(to right, white 0%, rgba(255, 255, 255, 0) 100%)",
            content:"''",
            height: "100%",
            width: 100,
            position: "absolute",
            zIndex: 2,
            top: 0,
            left: 0
        },
        "::after": {
            background: "linear-gradient(to right, white 0%, rgba(255, 255, 255, 0) 100%)",
            content:"''",
            height: "100%",
            width: 100,
            position: "absolute",
            zIndex: 2,
            top: 0,
            right: 0,
            transform: "rotateZ(180deg)"
        },
    }),
    "div",
    ["id"]
);
